import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Loader2, AlertTriangle, CheckCircle2, CalendarClock } from "lucide-react";

const AdminFutureJobs = ({ onAuthError }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => adminApi.get("/admin/future-jobs/status").then((r) => setData(r.data)).catch((e) => { onAuthError?.(e); toast.error("Could not load the trend lists"); });

  useEffect(() => { load(); }, []);

  const markRefreshed = async (key) => {
    setBusy(key);
    try {
      await adminApi.post(`/admin/future-jobs/${key}/refreshed`);
      await load();
      toast.success("Refresh date updated");
    } catch (e) {
      onAuthError?.(e);
      toast.error("Could not update that list");
    } finally { setBusy(""); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-future-jobs">
      <div className={`accent-bar rounded-xl border p-4 pl-5 ${data.overdue_count ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} style={{ "--accent": data.overdue_count ? "#D97706" : "#059669" }}>
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          {data.overdue_count ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {data.overdue_count ? `${data.overdue_count} trend list${data.overdue_count > 1 ? "s need" : " needs"} refreshing` : "Every trend list is current"}
        </p>
        <p className="mt-1 text-xs text-slate-600">
          Refresh each list every {data.refresh_every_days} days: send the new fact-checked trend PDF to the product team, or publish the
          updated list through the Future Jobs publish API. The countdown restarts the moment a list is published or marked refreshed.
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">List</th>
              <th className="px-4 py-3">Roles</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Last refreshed</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.editions.map((e) => (
              <tr key={e.key} data-testid={`trend-list-${e.key}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{e.label}</p>
                  <p className="text-[11px] text-slate-500">{e.region} · {e.family === "ai" ? "AI" : "Non-AI"}{e.active ? "" : " · switched off"}</p>
                </td>
                <td className="num-display px-4 py-3 text-slate-700">{e.roles}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{e.source === "admin" ? "Published by admin" : "Ships with product"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{e.refreshed_at ? new Date(e.refreshed_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  {e.overdue ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> {e.age_days} days old</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CalendarClock className="h-3 w-3" /> due in {e.due_in_days} days</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="outline" size="sm" disabled={busy === e.key} onClick={() => markRefreshed(e.key)} data-testid={`mark-refreshed-${e.key}`}>
                    {busy === e.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Mark refreshed"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminFutureJobs;
